/**
 * Parse Redatam tables with three or more dimensions into a flat structure
 * that can be consumed by MUI's DataGrid component. The parser uses:
 *   - dimension[0] as the set of value columns (one column per entry),
 *   - dimension[last] as the leading row descriptor,
 *   - the remaining dimensions (dimension[1..last-1]) as additional row descriptors,
 *     keeping their relative order after the leading dimension.
 *
 * If the payload includes an area break, the parser adds an initial column
 * with the area label and repeats the rows per area.
 */

const toStringSafe = (value) => {
	if (value === null || value === undefined) return '';
	return String(value).trim();
};

const normaliseLabel = (label, fallback) => {
	const base = toStringSafe(label);
	return base || fallback;
};

const buildFieldId = (prefix, dimensionIndex, entryIndex = null) => {
	const suffix = entryIndex === null ? `${dimensionIndex}` : `${dimensionIndex}_${entryIndex}`;
	return `${prefix}_${suffix}`;
};

const buildDimensionInfo = (dimension, index) => {
	const label = normaliseLabel(dimension?.label, `Dimension ${index + 1}`);
	const alias = normaliseLabel(dimension?.alias, normaliseLabel(dimension?.name, `dim${index + 1}`));

	let total =
		Array.isArray(dimension?.values) && dimension.values.length
			? dimension.values.length
			: Array.isArray(dimension?.labels) && dimension.labels.length
			? dimension.labels.length
			: Number.isFinite(dimension?.total)
			? Number(dimension.total)
			: 0;

	if (total < 0) total = 0;

	const entries = Array.from({ length: total }, (_, entryIdx) => {
		const value = dimension?.values?.[entryIdx];
		const entryLabel = toStringSafe(dimension?.labels?.[entryIdx]);

		const labelValue = entryLabel || toStringSafe(value) || `${label} ${entryIdx + 1}`;

		const idValue = toStringSafe(value) || `${alias}_${entryIdx}`;

		return { index: entryIdx, label: labelValue, id: idValue };
	});

	return {
		index,
		label,
		alias,
		entries,
		size: entries.length,
	};
};

const computeStrides = (sizes) => {
	// Calculate strides for row-major order (C-style)
	// Data is stored as: [row0col0ctrl0, row0col0ctrl1, row0col1ctrl0, ...]
	const strides = new Array(sizes.length).fill(1);
	let multiplier = 1;

	// Start from the rightmost (last) dimension and move left
	for (let i = sizes.length - 1; i >= 0; i--) {
		strides[i] = multiplier;
		multiplier *= sizes[i];
	}

	return strides;
};

const computeFlatIndex = (indices, strides) => {
	return indices.reduce((acc, value, idx) => acc + value * strides[idx], 0);
};

export default function multiDimensionParse(dataJSON, dimensionOrder = null) {
	const title = dataJSON?.metadata?.title || '';
	let dimensions = Array.isArray(dataJSON?.metadata?.dimensions)
		? dataJSON.metadata.dimensions
		: [];

	if (dimensions.length < 3) {
		return {
			title,
			columns: [],
			data: [],
		};
	}

	// Store original dimensions for stride calculation (data is stored in original order)
	const originalDimensions = dimensions;

	// Apply custom dimension order if specified
	// dimensionOrder is an array of indices, e.g., [1, 0, 2] swaps first two dimensions
	// If null/undefined, use original order
	if (dimensionOrder && Array.isArray(dimensionOrder)) {
		const reorderedDimensions = [];
		for (let i = 0; i < dimensionOrder.length && i < dimensions.length; i++) {
			const targetIndex = dimensionOrder[i];
			if (targetIndex >= 0 && targetIndex < dimensions.length) {
				reorderedDimensions.push(dimensions[targetIndex]);
			}
		}
		// Use reordered dimensions if valid
		if (reorderedDimensions.length === dimensions.length) {
			dimensions = reorderedDimensions;
		}
	}

	const dimensionInfos = dimensions.map(buildDimensionInfo);
	const originalDimensionInfos = originalDimensions.map(buildDimensionInfo);

	if (dimensionInfos.some((info) => info.size === 0)) {
		return {
			title,
			columns: [],
			data: [],
		};
	}

	const columnDimension = dimensionInfos[0];
	const secondaryDimensions = dimensionInfos.slice(1);

	if (!secondaryDimensions.length) {
		return {
			title,
			columns: [],
			data: [],
		};
	}

	const primaryRowDimension = secondaryDimensions[secondaryDimensions.length - 1];
	const remainingRowDimensions = secondaryDimensions.slice(0, -1);
	const rowDimensions = [primaryRowDimension, ...remainingRowDimensions];

	const rowFieldDefinitions = rowDimensions.map((dimInfo) => ({
		title: dimInfo.label,
		field: buildFieldId('row', dimInfo.index),
		dimensionIndex: dimInfo.index,
	}));

	const columnFieldDefinitions = columnDimension.entries.map((entry, entryIdx) => ({
		title: entry.label,
		field: buildFieldId('col', columnDimension.index, entryIdx),
		entryIndex: entryIdx,
		dimensionIndex: columnDimension.index,
	}));

	const hasAreaBreak = Boolean(dataJSON?.metadata?.areabreak);
	const areaFieldLabel =
		toStringSafe(dataJSON?.metadata?.areabreak?.entity) ||
		toStringSafe(dataJSON?.metadata?.areabreak?.label) ||
		'Area';
	const areas = Array.isArray(dataJSON?.metadata?.areas) ? dataJSON.metadata.areas : [];

	// Calculate strides based on ORIGINAL dimension order (data is stored in original order)
	const originalSizes = originalDimensionInfos.map((info) => info.size);
	const originalStrides = computeStrides(originalSizes);

	// Create a mapping from reordered dimension index to original index
	const dimensionIndexMap = new Map();
	dimensionInfos.forEach((info) => {
		// Find this dimension in the original list
		const originalIndex = originalDimensionInfos.findIndex(
			(origInfo) => origInfo.label === info.label && origInfo.size === info.size
		);
		if (originalIndex !== -1) {
			dimensionIndexMap.set(info.index, originalIndex);
		}
	});

	const baseColumns = [];
	const areaFieldId = hasAreaBreak ? buildFieldId('area', -1) : null;

	if (hasAreaBreak) {
		baseColumns.push({
			title: areaFieldLabel,
			field: areaFieldId,
		});
	}

	rowFieldDefinitions.forEach((rowField) => {
		baseColumns.push({
			title: rowField.title,
			field: rowField.field,
		});
	});

	if (!columnFieldDefinitions.length) {
		return {
			title,
			columns: baseColumns,
			data: [],
		};
	}

	const fieldList = [
		...baseColumns,
		...columnFieldDefinitions.map(({ title, field }) => ({ title, field })),
		{
			title: 'Total',
			field: 'rowTotal',
		},
	];

	const datasets = hasAreaBreak ? dataJSON?.data || [] : [dataJSON?.data || []];
	const rowList = [];
	const rowFieldMap = new Map(rowFieldDefinitions.map((field) => [field.dimensionIndex, field]));

	const buildRowCombinations = (dims) => {
		if (!dims.length) return [[]];

		const [firstDim, ...restDims] = dims;
		const restCombinations = buildRowCombinations(restDims);
		const combinations = [];

		firstDim.entries.forEach((entry) => {
			restCombinations.forEach((restCombo) => {
				combinations.push([
					{ dimensionIndex: firstDim.index, entry },
					...restCombo,
				]);
			});
		});

		return combinations;
	};

	const rowCombinations = buildRowCombinations(rowDimensions);

	datasets.forEach((dataset, datasetIndex) => {
		if (!Array.isArray(dataset)) return;

		const areaLabel =
			hasAreaBreak
				? toStringSafe(areas?.[datasetIndex]?.label) || `${areaFieldLabel} ${datasetIndex + 1}`
				: null;

		rowCombinations.forEach((combination) => {
			const indices = new Array(dimensionInfos.length).fill(0);
			const row = {};

			if (hasAreaBreak) {
				row[areaFieldId] = areaLabel;
			}

			combination.forEach(({ dimensionIndex, entry }) => {
				indices[dimensionIndex] = entry.index;
				const rowField = rowFieldMap.get(dimensionIndex);
				if (rowField) {
					row[rowField.field] = entry.label;
				}
			});

			let rowTotal = 0;
			columnFieldDefinitions.forEach((columnFieldDef) => {
				const indicesForValue = [...indices];
				const columnEntry = columnDimension.entries[columnFieldDef.entryIndex];

				if (!columnEntry) {
					row[columnFieldDef.field] = null;
					return;
				}

				indicesForValue[columnDimension.index] = columnEntry.index;

				// Map reordered indices back to original dimension order for data access
				const originalIndices = new Array(originalDimensionInfos.length).fill(0);
				indicesForValue.forEach((value, reorderedIdx) => {
					const originalIdx = dimensionIndexMap.get(reorderedIdx);
					if (originalIdx !== undefined) {
						originalIndices[originalIdx] = value;
					}
				});

				const flatIndex = computeFlatIndex(originalIndices, originalStrides);
				const value = flatIndex >= 0 && flatIndex < dataset.length ? dataset[flatIndex] : null;

				row[columnFieldDef.field] = value ?? null;

				// Sum up numeric values for the total
				if (typeof value === 'number' && !isNaN(value)) {
					rowTotal += value;
				}
			});

			row.rowTotal = rowTotal;
			rowList.push(row);
		});
	});

	return {
		title,
		columns: fieldList,
		data: rowList,
	};
}
